import {
  Control,
  FieldErrors,
  useFieldArray,
  UseFormRegister,
} from 'react-hook-form'
import { Button, MultiSelectStoreDropdown, TextInput } from 'components/common'
import { CloseIcon, PlusIcon } from 'components/icons'
import { TalabatConfigFormFields } from 'utils/schema/talabatConfigSchema'

type Props = {
  aggregatorIndex: number
  aggregatorId: string
  vendorMapKey?: string
  register: UseFormRegister<TalabatConfigFormFields>
  control: Control<TalabatConfigFormFields>
  setValue: any
  errors: FieldErrors<TalabatConfigFormFields>
}

// Repeatable list of chains for one Talabat aggregator. Each chain has its
// own Chain ID and its own explicit list of mapped stores — stores are no
// longer derived from the aggregator's `api.stores`.
const TalabatAggregatorChains = ({
  aggregatorIndex,
  aggregatorId,
  vendorMapKey,
  register,
  control,
  setValue,
  errors,
}: Props) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `aggregators.${aggregatorIndex}.chains`,
  })

  const chainErrors = errors?.aggregators?.[aggregatorIndex]?.chains

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-3">
      <div className="mb-3 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-800">{aggregatorId}</p>
          <p className="text-xs text-gray-500">
            {fields.length} chain{fields.length === 1 ? '' : 's'} configured
            {vendorMapKey && (
              <>
                {' '}
                &middot; Vendor Map Key:{' '}
                <span className="font-medium text-gray-600">
                  {vendorMapKey}
                </span>
              </>
            )}
          </p>
        </div>

        <Button
          type="button"
          variant="custom"
          icon={<PlusIcon />}
          buttonText="Add Chain"
          className="flex items-center gap-1.5 rounded-md border border-button-primary px-2.5 py-1.5 text-xs font-semibold text-button-primary transition-colors hover:bg-button-primary hover:text-white"
          onClick={() =>
            append({
              chainId: '',
              authClientId: '',
              authClientSecret: '',
              stores: [],
            })
          }
        />
      </div>

      {fields.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-5 text-center">
          <p className="text-sm text-gray-400">
            No chains configured for this aggregator.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {fields.map((field, chainIndex) => (
          <div
            key={field.id}
            className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-2.5 mid-md:flex-row mid-md:items-start"
          >
            <div className="w-full mid-md:flex-1">
              <TextInput
                required
                name={`aggregators.${aggregatorIndex}.chains.${chainIndex}.chainId`}
                register={register}
                label="Chain ID"
                placeholder="Enter chain ID"
                error={chainErrors?.[chainIndex]?.chainId?.message}
              />
            </div>

            <div className="w-full mid-md:flex-1">
              <TextInput
                required
                name={`aggregators.${aggregatorIndex}.chains.${chainIndex}.authClientId`}
                register={register}
                label="Auth Client ID"
                placeholder="Enter client ID"
                error={chainErrors?.[chainIndex]?.authClientId?.message}
              />
            </div>

            <div className="w-full mid-md:flex-1">
              <TextInput
                required
                type="password"
                name={`aggregators.${aggregatorIndex}.chains.${chainIndex}.authClientSecret`}
                register={register}
                label="Auth Client Secret"
                placeholder="Enter client secret"
                error={chainErrors?.[chainIndex]?.authClientSecret?.message}
              />
            </div>

            <div className="w-full mid-md:flex-1">
              <MultiSelectStoreDropdown
                required
                resetForm
                label="Stores"
                name={`aggregators.${aggregatorIndex}.chains.${chainIndex}.stores`}
                setValue={setValue}
                defaultSelected={(field as any).stores}
                error={chainErrors?.[chainIndex]?.stores?.message as string}
                isActive
              />
            </div>

            <div className="flex shrink-0 justify-end mid-md:pt-6">
              <button
                type="button"
                aria-label="Remove chain"
                onClick={() => remove(chainIndex)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-400 transition-colors hover:border-error/40 hover:bg-error/10 hover:text-error"
              >
                <span className="h-3 w-3">
                  <CloseIcon />
                </span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default TalabatAggregatorChains
